import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, X, RotateCw, Play, ChevronLeft, Minus, Plus, Maximize2,
  Layers, Trash2, Copy, Settings2, FileDown, FileCode2,
} from 'lucide-react';
import {
  parseDxfContent, computeBoundingBox, entityToSvgPaths, nestParts,
  type DxfPart, type NestingResult, type Placement,
} from '../utils/dxfNesting';
import { buildNestedDxfString } from '../utils/dxfExportNested';
import { nestPartsShape } from '../utils/dxfRasterNest';
import './DxfNestingPage.css';

const PART_COLORS = [
  '#00d4aa', '#ff6b6b', '#ffd93d', '#4d96ff', '#c084fc',
  '#fb923c', '#22d3ee', '#a3e635', '#f472b6', '#60a5fa',
];

interface PartEntry {
  id: string;
  name: string;
  dxfPart: DxfPart;
  quantity: number;
  color: string;
}

const DxfNestingPage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [parts, setParts] = useState<PartEntry[]>([]);
  const [sheetWidth, setSheetWidth] = useState(2440);
  const [sheetHeight, setSheetHeight] = useState(1220);
  const [spacing, setSpacing] = useState(3);
  const [allowRotation, setAllowRotation] = useState(true);
  /** When true, collision uses rasterized geometry (tighter). When false, axis-aligned bounding boxes (faster). */
  const [useShapeNesting, setUseShapeNesting] = useState(true);
  const [unit, setUnit] = useState<'in' | 'mm'>('mm');
  const [result, setResult] = useState<NestingResult | null>(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredPlacement, setHoveredPlacement] = useState<string | null>(null);

  const MM_PER_IN = 25.4;

  const switchUnit = useCallback((newUnit: 'in' | 'mm') => {
    if (newUnit === unit) return;
    setResult(null);
    if (newUnit === 'mm') {
      setSheetWidth(w => Math.round(w * MM_PER_IN * 10) / 10);
      setSheetHeight(h => Math.round(h * MM_PER_IN * 10) / 10);
      setSpacing(s => Math.round(s * MM_PER_IN * 100) / 100);
    } else {
      setSheetWidth(w => Math.round((w / MM_PER_IN) * 100) / 100);
      setSheetHeight(h => Math.round((h / MM_PER_IN) * 100) / 100);
      setSpacing(s => Math.round((s / MM_PER_IN) * 10000) / 10000);
    }
    setUnit(newUnit);
  }, [unit]);

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    setError(null);
    const newParts: PartEntry[] = [];
    for (const file of Array.from(files)) {
      if (!file.name.toLowerCase().endsWith('.dxf')) {
        setError(`Skipped "${file.name}" — not a .dxf file`);
        continue;
      }
      try {
        const text = await file.text();
        const { entities, blocks } = parseDxfContent(text);
        if (entities.length === 0) { setError(`"${file.name}" contains no geometry`); continue; }
        const allEntities = [...entities];
        if (blocks) {
          for (const bk of Object.values(blocks) as any[]) {
            if (bk?.entities) allEntities.push(...bk.entities);
          }
        }
        const bbox = computeBoundingBox(entities, blocks);
        if (bbox.width === 0 && bbox.height === 0) { setError(`"${file.name}" has zero-size geometry`); continue; }
        const entityCounts: Record<string, number> = {};
        allEntities.forEach((e: any) => { entityCounts[e.type] = (entityCounts[e.type] || 0) + 1; });
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const colorIdx = (parts.length + newParts.length) % PART_COLORS.length;
        newParts.push({
          id, name: file.name.replace(/\.dxf$/i, ''), quantity: 1,
          color: PART_COLORS[colorIdx],
          dxfPart: { id, name: file.name.replace(/\.dxf$/i, ''), rawEntities: entities, blocks, bbox, entityCounts },
        });
      } catch (err: any) {
        setError(`Failed to parse "${file.name}": ${err.message}`);
      }
    }
    if (newParts.length) {
      setParts(prev => [...prev, ...newParts]);
      setResult(null);
    }
  }, [parts.length]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const updateQuantity = (id: string, qty: number) => {
    setParts(prev => prev.map(p => p.id === id ? { ...p, quantity: Math.max(1, qty) } : p));
    setResult(null);
  };

  const removePart = (id: string) => {
    setParts(prev => prev.filter(p => p.id !== id));
    setResult(null);
  };

  const runNesting = useCallback(() => {
    if (!parts.length) return;
    setError(null);
    try {
      const r = useShapeNesting
        ? nestPartsShape(
            parts.map(p => ({ part: p.dxfPart, quantity: p.quantity })),
            { sheetWidth, sheetHeight, spacing, allowRotation },
          )
        : nestParts(
            parts.map(p => ({ part: p.dxfPart, quantity: p.quantity })),
            sheetWidth, sheetHeight, spacing, allowRotation,
          );
      setResult(r);
      setActiveSheet(0);
      if (r.placedParts < r.totalParts) {
        setError(`${r.totalParts - r.placedParts} part(s) could not fit on any sheet — check your sheet dimensions.`);
      }
    } catch (err: any) {
      setError(`Nesting failed: ${err.message}`);
    }
  }, [parts, sheetWidth, sheetHeight, spacing, allowRotation, useShapeNesting]);

  const fmt = (v: number) => unit === 'in' ? `${v.toFixed(2)}"` : `${v.toFixed(1)} mm`;

  const partMap = useMemo(() => {
    const m = new Map<string, PartEntry>();
    parts.forEach(p => m.set(p.id, p));
    return m;
  }, [parts]);

  const totalPieces = parts.reduce((s, p) => s + p.quantity, 0);

  const currentSheet = result?.sheets[activeSheet];

  const svgPadding = unit === 'mm' ? Math.max(sheetWidth, sheetHeight) * 0.04 : 8;
  const vbW = sheetWidth + svgPadding * 2;
  const vbH = sheetHeight + svgPadding * 2;

  const gridStep = useMemo(() => {
    const maxDim = Math.max(sheetWidth, sheetHeight);
    if (unit === 'mm') {
      if (maxDim <= 100) return 10;
      if (maxDim <= 500) return 50;
      if (maxDim <= 1500) return 100;
      return Math.ceil(maxDim / 20 / 50) * 50;
    }
    if (maxDim <= 24) return 1;
    if (maxDim <= 60) return 6;
    if (maxDim <= 200) return 12;
    return Math.ceil(maxDim / 20);
  }, [sheetWidth, sheetHeight, unit]);

  const renderPartPreview = (part: DxfPart, color: string) => {
    const { bbox, rawEntities, blocks } = part;
    const pad = Math.max(bbox.width, bbox.height) * 0.08;
    const vb = `${-pad} ${-pad} ${bbox.width + pad * 2} ${bbox.height + pad * 2}`;
    const sw = Math.max(bbox.width, bbox.height) / 40;
    return (
      <svg viewBox={vb} className="dxf-part-preview" preserveAspectRatio="xMidYMid meet">
        {rawEntities.map((e: any, i: number) =>
          entityToSvgPaths(e, bbox.minX, bbox.minY, bbox.height, blocks).map((d, j) => (
            <path key={`${i}-${j}`} d={d} fill="none" stroke={color} strokeWidth={sw} />
          ))
        )}
      </svg>
    );
  };

  const renderPlacedPart = (pl: Placement) => {
    const entry = partMap.get(pl.partId);
    if (!entry) return null;
    const { dxfPart, color } = entry;
    const { bbox, rawEntities, blocks } = dxfPart;
    const sw = Math.max(bbox.width, bbox.height) / 120;
    const key = `${pl.partId}-${pl.copyIndex}`;
    const isHovered = hoveredPlacement === key;
    const labelFs = Math.min(3.2, Math.max(1.4, Math.min(pl.width, pl.height) * 0.032));
    const shortLabel = entry.name.length > 16 ? `${entry.name.slice(0, 14)}…` : entry.name;
    const tip = `${entry.name} · copy ${pl.copyIndex + 1} of ${entry.quantity}`;

    return (
      <g
        key={key}
        transform={`translate(${svgPadding + pl.x}, ${svgPadding + pl.y})`}
        onMouseEnter={() => setHoveredPlacement(key)}
        onMouseLeave={() => setHoveredPlacement(null)}
        style={{ cursor: 'pointer' }}
      >
        <title>{tip}</title>
        {isHovered && (
          <rect
            x={0} y={0} width={pl.width} height={pl.height}
            fill="none"
            stroke={color}
            strokeWidth={Math.max(0.35, svgPadding * 0.012)}
            strokeDasharray={`${svgPadding * 0.08} ${svgPadding * 0.05}`}
            rx={svgPadding * 0.015}
            opacity={0.85}
          />
        )}
        <g transform={pl.rotated ? `translate(${pl.width}, 0) rotate(90)` : undefined}>
          {rawEntities.map((e: any, i: number) =>
            entityToSvgPaths(e, bbox.minX, bbox.minY, bbox.height, blocks).map((d, j) => (
              <path key={`${i}-${j}`} d={d} fill="none" stroke={color} strokeWidth={sw}
                strokeOpacity={isHovered ? 1 : 0.85} />
            ))
          )}
        </g>
        <text
          x={labelFs * 0.35}
          y={labelFs + labelFs * 0.35}
          fill={color}
          fillOpacity={isHovered ? 0.95 : 0.55}
          fontSize={labelFs}
          textAnchor="start"
          fontFamily="'JetBrains Mono', monospace"
          fontWeight={500}
          style={{ pointerEvents: 'none' }}
        >
          {shortLabel}
        </text>
      </g>
    );
  };

  const handleExportSVG = () => {
    if (!currentSheet) return;
    const svgEl = document.querySelector('.dxf-sheet-svg');
    if (!svgEl) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svgEl);
    const blob = new Blob([svgStr], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `nesting-sheet-${activeSheet + 1}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportDxf = useCallback(() => {
    if (!currentSheet || !result) return;
    const partById = new Map(parts.map(p => [p.id, p.dxfPart]));
    const str = buildNestedDxfString({
      sheetWidth,
      sheetHeight,
      placements: currentSheet.placements,
      partById,
      insunits: unit === 'mm' ? 4 : 1,
    });
    const blob = new Blob([str], { type: 'application/dxf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nesting-sheet-${activeSheet + 1}.dxf`;
    a.click();
    URL.revokeObjectURL(url);
  }, [currentSheet, result, parts, sheetWidth, sheetHeight, activeSheet, unit]);

  return (
    <div className="dxf-nesting-page">
      {/* Header */}
      <header className="dxf-header">
        <div className="dxf-header-left">
          <button className="dxf-back-btn" onClick={() => navigate(-1)}>
            <ChevronLeft size={18} />
          </button>
          <div className="dxf-header-title">
            <Layers size={18} className="dxf-header-icon" />
            <h1>DXF Nesting</h1>
          </div>
        </div>
        <div className="dxf-header-right">
          <span className="dxf-unit-hint">DXF &amp; sheet units</span>
          <div className="dxf-unit-toggle">
            <button className={unit === 'mm' ? 'active' : ''} onClick={() => switchUnit('mm')}>mm</button>
            <button className={unit === 'in' ? 'active' : ''} onClick={() => switchUnit('in')}>in</button>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside className="dxf-sidebar">
        <div
          className={`dxf-upload-zone ${dragOver ? 'dragover' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <Upload size={20} className="dxf-upload-icon" />
          <span className="dxf-upload-text">Drop .dxf files or click to browse</span>
          <input
            ref={fileInputRef} type="file" accept=".dxf" multiple hidden
            onChange={e => { if (e.target.files?.length) handleFiles(e.target.files); e.target.value = ''; }}
          />
        </div>

        {error && (
          <div className="dxf-error">
            <span>{error}</span>
            <button onClick={() => setError(null)}><X size={14} /></button>
          </div>
        )}

        {parts.length > 0 && (
          <>
            <div className="dxf-section-label">
              Parts <span className="dxf-section-count">{parts.length} file{parts.length !== 1 ? 's' : ''} · {totalPieces} piece{totalPieces !== 1 ? 's' : ''}</span>
            </div>
            <div className="dxf-parts-list">
              {parts.map(p => (
                <div key={p.id} className="dxf-part-item" style={{ borderLeftColor: p.color }}>
                  <div className="dxf-part-preview-wrap">
                    {renderPartPreview(p.dxfPart, p.color)}
                  </div>
                  <div className="dxf-part-info">
                    <div className="dxf-part-name">{p.name}</div>
                    <div className="dxf-part-dims">
                      {fmt(p.dxfPart.bbox.width)} × {fmt(p.dxfPart.bbox.height)}
                    </div>
                    <div className="dxf-part-entities">
                      {Object.entries(p.dxfPart.entityCounts).map(([t, c]) => `${c} ${t}`).join(', ')}
                    </div>
                  </div>
                  <div className="dxf-part-qty">
                    <button onClick={() => updateQuantity(p.id, p.quantity - 1)}><Minus size={12} /></button>
                    <input
                      type="number" min={1} value={p.quantity}
                      onChange={e => updateQuantity(p.id, parseInt(e.target.value) || 1)}
                    />
                    <button onClick={() => updateQuantity(p.id, p.quantity + 1)}><Plus size={12} /></button>
                  </div>
                  <button className="dxf-part-remove" onClick={() => removePart(p.id)}>
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="dxf-section-label"><Settings2 size={13} /> Sheet Settings</div>
        <div className="dxf-settings-grid">
          <label>
            <span>Width</span>
            <div className="dxf-input-unit">
              <input type="number" value={sheetWidth} min={1} step={unit === 'in' ? 0.5 : 1}
                onChange={e => { setSheetWidth(parseFloat(e.target.value) || 1); setResult(null); }} />
              <span className="dxf-unit-label">{unit}</span>
            </div>
          </label>
          <label>
            <span>Height</span>
            <div className="dxf-input-unit">
              <input type="number" value={sheetHeight} min={1} step={unit === 'in' ? 0.5 : 1}
                onChange={e => { setSheetHeight(parseFloat(e.target.value) || 1); setResult(null); }} />
              <span className="dxf-unit-label">{unit}</span>
            </div>
          </label>
          <label>
            <span>Spacing</span>
            <div className="dxf-input-unit">
              <input type="number" value={spacing} min={0} step={unit === 'in' ? 0.0625 : 0.5}
                onChange={e => { setSpacing(parseFloat(e.target.value) || 0); setResult(null); }} />
              <span className="dxf-unit-label">{unit}</span>
            </div>
          </label>
        </div>

        <label className="dxf-checkbox">
          <input type="checkbox" checked={allowRotation}
            onChange={e => { setAllowRotation(e.target.checked); setResult(null); }} />
          <RotateCw size={13} />
          <span>Allow 90° rotation</span>
        </label>

        <label className="dxf-checkbox dxf-checkbox-highlight">
          <input type="checkbox" checked={useShapeNesting}
            onChange={e => { setUseShapeNesting(e.target.checked); setResult(null); }} />
          <Layers size={13} />
          <span>Nest by actual shape (not just bounding box)</span>
        </label>
        {useShapeNesting && (
          <p className="dxf-shape-hint">
            Uses a grid over your real outlines so parts can sit closer where contours allow. Slightly slower on large sheets.
          </p>
        )}

        <button
          className="dxf-nest-btn"
          onClick={runNesting}
          disabled={parts.length === 0}
        >
          <Play size={15} />
          Run Nesting
        </button>

        {result && (
          <div className="dxf-result-stats">
            <div className="dxf-stat-row">
              <span className="dxf-stat-label">Sheets</span>
              <span className="dxf-stat-value">{result.totalSheets}</span>
            </div>
            <div className="dxf-stat-row">
              <span className="dxf-stat-label">Placed</span>
              <span className="dxf-stat-value">{result.placedParts} / {result.totalParts}</span>
            </div>
            <div className="dxf-stat-row">
              <span className="dxf-stat-label">Utilization</span>
              <span className="dxf-stat-value dxf-stat-highlight">{result.overallUtilization.toFixed(1)}%</span>
            </div>
            <div className="dxf-utilization-bar">
              <div className="dxf-utilization-fill" style={{ width: `${Math.min(result.overallUtilization, 100)}%` }} />
            </div>
          </div>
        )}
      </aside>

      {/* Canvas */}
      <main className="dxf-canvas-area">
        {result && result.sheets.length > 0 ? (
          <>
            <div className="dxf-canvas-toolbar">
              <div className="dxf-sheet-tabs">
                {result.sheets.map((sh, i) => (
                  <button
                    key={i}
                    className={`dxf-sheet-tab ${activeSheet === i ? 'active' : ''}`}
                    onClick={() => setActiveSheet(i)}
                  >
                    Sheet {i + 1}
                    <span className="dxf-sheet-tab-util">{sh.utilization.toFixed(0)}%</span>
                  </button>
                ))}
              </div>
              <div className="dxf-zoom-controls">
                <button onClick={handleExportDxf} className="dxf-export-btn" title="Download nested DXF (geometry on sheet)">
                  <FileCode2 size={14} />
                </button>
                <button onClick={handleExportSVG} className="dxf-export-btn" title="Export SVG preview">
                  <FileDown size={14} />
                </button>
                <button onClick={() => setZoom(z => Math.max(0.25, z - 0.25))} title="Zoom out"><Minus size={14} /></button>
                <span className="dxf-zoom-label">{(zoom * 100).toFixed(0)}%</span>
                <button onClick={() => setZoom(z => Math.min(4, z + 0.25))} title="Zoom in"><Plus size={14} /></button>
                <button onClick={() => setZoom(1)} title="Fit to view"><Maximize2 size={14} /></button>
              </div>
            </div>

            <div className="dxf-svg-container">
              <svg
                className="dxf-sheet-svg"
                viewBox={`0 0 ${vbW} ${vbH}`}
                preserveAspectRatio="xMidYMid meet"
                style={{ width: `${zoom * 100}%`, maxWidth: `${zoom * 100}%` }}
              >
                {/* Sheet background */}
                <rect x={svgPadding} y={svgPadding} width={sheetWidth} height={sheetHeight}
                  fill="#141922" stroke="#2d3548" strokeWidth={svgPadding * 0.03} rx={svgPadding * 0.02} />

                {/* Grid */}
                {Array.from({ length: Math.floor(sheetWidth / gridStep) + 1 }, (_, i) => (
                  <line key={`gv${i}`}
                    x1={svgPadding + i * gridStep} y1={svgPadding}
                    x2={svgPadding + i * gridStep} y2={svgPadding + sheetHeight}
                    stroke="#1e2636" strokeWidth={svgPadding * 0.01} />
                ))}
                {Array.from({ length: Math.floor(sheetHeight / gridStep) + 1 }, (_, i) => (
                  <line key={`gh${i}`}
                    x1={svgPadding} y1={svgPadding + i * gridStep}
                    x2={svgPadding + sheetWidth} y2={svgPadding + i * gridStep}
                    stroke="#1e2636" strokeWidth={svgPadding * 0.01} />
                ))}

                {/* Dimension labels */}
                <text x={svgPadding + sheetWidth / 2} y={svgPadding * 0.35}
                  fill="#4a5568" fontSize={svgPadding * 0.5} textAnchor="middle"
                  fontFamily="'JetBrains Mono', monospace">
                  {fmt(sheetWidth)}
                </text>
                <text
                  x={svgPadding * 0.35} y={svgPadding + sheetHeight / 2}
                  fill="#4a5568" fontSize={svgPadding * 0.5} textAnchor="middle"
                  fontFamily="'JetBrains Mono', monospace"
                  transform={`rotate(-90, ${svgPadding * 0.35}, ${svgPadding + sheetHeight / 2})`}
                >
                  {fmt(sheetHeight)}
                </text>

                {/* Placed parts */}
                {currentSheet?.placements.map(renderPlacedPart)}
              </svg>
            </div>

            {currentSheet && (
              <div className="dxf-canvas-footer">
                <span>
                  <Copy size={12} /> {currentSheet.placements.length} part{currentSheet.placements.length !== 1 ? 's' : ''} on this sheet
                </span>
                <span className="dxf-footer-util">
                  Sheet utilization: <strong>{currentSheet.utilization.toFixed(1)}%</strong>
                </span>
                <span className="dxf-footer-waste">
                  Waste: <strong>{(100 - currentSheet.utilization).toFixed(1)}%</strong>
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="dxf-empty-canvas">
            <div className="dxf-empty-content">
              <Layers size={48} strokeWidth={1} />
              <h2>DXF Nesting Tool</h2>
              <p>Upload DXF files, set quantities and sheet dimensions, then run the nesting algorithm to optimize part layout.</p>
              <div className="dxf-empty-steps">
                <div className="dxf-step"><span>1</span> Upload .dxf part files</div>
                <div className="dxf-step"><span>2</span> Set quantities &amp; sheet size</div>
                <div className="dxf-step"><span>3</span> Click "Run Nesting"</div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DxfNestingPage;
